/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * Writing a course out as a GPX file.
 *
 * GPX 1.1 with one track (`<trk>`) — the shape Garmin Connect, Strava, Coros
 * and Komoot all import as a course, and the first shape parseGpx reads back,
 * so a file downloaded here round-trips into the event form unchanged.
 *
 * Hand-written rather than a library: it is one XML template, and the only
 * thing that needs care is escaping the course name.
 */

const escapeXml = (value) => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&apos;');

/** Six decimals is ~11 cm — finer than any consumer GPS. */
// const coord = (value) => Number(value).toFixed(6);
/**
 * A coordinate exactly as held — never rounded here — so the file carries the
 * same numbers as the line stored for the map. Drawn points are already rounded
 * to 6 decimals when placed (RouteDrawer); an uploaded file keeps its own
 * precision. Exponent notation (1e-7) is not valid in GPX, so a value that tiny
 * is written out in full.
 */
export function coord(value) {
  const n = Number(value);
  const text = String(n);
  if (!/e/i.test(text)) return text;
  return n.toFixed(12).replace(/0+$/, '').replace(/\.$/, '');
}

export function buildGpx(points, name = 'Course') {
  const title = escapeXml(name || 'Course');
  const trkpts = (points || [])
    .map((p) => `      <trkpt lat="${coord(p.lat)}" lon="${coord(p.lng)}"></trkpt>`)
    .join('\n');
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<gpx version="1.1" creator="AthloHive Route Builder" xmlns="http://www.topografix.com/GPX/1/1">',
    `  <metadata><name>${title}</name></metadata>`,
    '  <trk>',
    `    <name>${title}</name>`,
    '    <trkseg>',
    trkpts,
    '    </trkseg>',
    '  </trk>',
    '</gpx>',
    '',
  ].join('\n');
}

/**
 * A safe file name from a course name: "Cubbon Park 5K loop" →
 * "cubbon-park-5k-loop.gpx". The storage server decides the file's category by
 * its extension, so `.gpx` is always there.
 */
export function gpxFileName(name) {
  const slug = String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `${slug || 'course'}.gpx`;
}

/** The course as a File, ready for the same pending-upload slot a picked file uses. */
export function gpxFile(points, name) {
  return new File([buildGpx(points, name)], gpxFileName(name), { type: 'application/gpx+xml' });
}

/** Saves the course to the organizer's computer. */
export function downloadGpx(points, name) {
  const blob = new Blob([buildGpx(points, name)], { type: 'application/gpx+xml' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = gpxFileName(name);
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick: some browsers start the download asynchronously
  // and a URL revoked synchronously can cancel it.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
